/** A comparison in its own address (2026-10-28, the traffic agent's proposal 3: an export and a comparison carry
 *  the site with them). The Calculator and Compare keep what they show in the address bar's query as it changes,
 *  so the address, the Copy link button and the link in an exported PDF's footer all open the same comparison.
 *  Nothing about the visitor is in it: a model, a workload, where each vendor is priced, the options picked. */
import { useState } from 'react'
import { BASE } from '../api'
import { ANY_REGION, type Provider, type RegionChoices } from '../types'
import { track } from './track'

// the address the site was opened at, read once: a page applies it only if it was opened at that page
const landing = new URLSearchParams(window.location.search)
const landingPath = window.location.pathname.replace(/\/+$/, '')

/** The query the site was opened with, when it was opened at this page and carries more than tags; else null. */
export function opened(page: string): URLSearchParams | null {
  return landingPath === `${BASE}${page}`.replace(/\/+$/, '') && [...landing.keys()].some((k) => !k.startsWith('utm_')) ? landing : null
}

/** Keep the page's state in the address bar, without a new history entry; nothing once the visitor has moved on. */
export function keepInAddress(page: string, params: Record<string, string | number | null | undefined>): void {
  const want = `${BASE}${page}`
  if (window.location.pathname !== want) return
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') q.set(k, String(v))
  const next = q.toString() ? `?${q}` : ''
  if (window.location.search !== next) window.history.replaceState(window.history.state, '', `${want}${next}`)
}

/** This page's address under the site's home, with its query: what an export's footer links back to. */
export function here(): string {
  const p = window.location.pathname
  return (p.startsWith(BASE) ? p.slice(BASE.length) : p.replace(/^\/+/, '')) + window.location.search
}

/** Where each vendor is priced, in a few characters: one choice for all ("any", "in-country", "in-country:DE"),
 *  else each vendor's ("aws:me-central-1,oci:any"). */
export function whereParam(regions: RegionChoices, vendors: Provider[]): string {
  const vals = vendors.map((p) => regions[p] ?? ANY_REGION)
  // "any" is written too: left out, the reader's own stored choice would price a different comparison
  if (vals.every((v) => v === vals[0])) return vals[0] ?? ANY_REGION
  return vendors.map((p, i) => `${p}:${vals[i]}`).join(',')
}

/** The region choices a `where` value sets, over the current ones; null when it sets nothing. */
export function applyWhere(param: string | null, current: RegionChoices, vendors: Provider[]): RegionChoices | null {
  if (!param) return null
  const next = { ...current }
  if (param.includes(',') || vendors.some((p) => param.startsWith(`${p}:`))) {
    for (const part of param.split(',')) {
      const at = part.indexOf(':')
      const p = part.slice(0, at) as Provider
      if (at > 0 && vendors.includes(p)) next[p] = part.slice(at + 1)
    }
  } else {
    for (const p of vendors) next[p] = param
  }
  return next
}

/** A round button beside Export: copies the address of this comparison, tagged as a shared link. */
export function CopyLink({ page }: { page: string }) {
  const [done, setDone] = useState(false)
  const copy = async () => {
    const u = new URL(window.location.href)
    u.searchParams.set('utm_source', 'share')
    u.searchParams.set('utm_medium', 'link')
    try { await navigator.clipboard.writeText(u.toString()) } catch { window.prompt('Copy this link', u.toString()) }
    track('share_link', page)
    setDone(true)
    window.setTimeout(() => setDone(false), 2000)
  }
  const name = done ? 'Link copied' : 'Copy a link to this comparison'
  return (
    <button type="button" className="xcircle" onClick={() => void copy()} title={name} aria-label={name}>
      {done ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M10 13a5 5 0 007.07 0l3-3a5 5 0 00-7.07-7.07l-1 1" /><path d="M14 11a5 5 0 00-7.07 0l-3 3a5 5 0 007.07 7.07l1-1" />
        </svg>
      )}
    </button>
  )
}
